/**
 * Контракты подбора: сессия, чат и Criteria Document (BE-070…BE-077).
 *
 * Зеркало `backend/app/modules/chat/schemas.py` и `criteria/document.py`.
 * Генерации типов из OpenAPI в проекте пока нет, поэтому контракт описан
 * руками и обязан совпадать с backend — расхождение ловит E2E на реальном стеке.
 *
 * Два правила чтения, которые нельзя «упростить»:
 *
 * * `content` реплики ассистента — **ключ i18n**, а не готовый текст: LLM
 *   пользовательских строк не пишет (CLAUDE.md §3 п. 6). Переводит фронт;
 * * «вопрос не задавался» и «пользователю всё равно» — разные состояния.
 *   Первое — `mode: 'unset'` и пустые списки, второе — `mode: 'any'`,
 *   `no_limit: true`, `specified: true`. Их нельзя схлопывать в «нет данных».
 */

import type { BodyType, Drivetrain, FuelType, Transmission } from '@entities/vehicle/types';

import type { OrderStage } from './stage';

export type { BodyType, Drivetrain, FuelType, Transmission };

// --- Справочники Criteria Document (MATCHING §3.3) --------------------------------------

export type BudgetMode = 'vehicle_only' | 'total';

/** `required` — кузов обязателен, `any` — пользователь сказал «неважно». */
export type BodyTypesMode = 'required' | 'any';

/** `any` — допустимо любое топливо, `allowed_set` — пользователь перечислил список. */
export type FuelMode = 'any' | 'allowed_set';

/** `unset` — вопрос не задавался; `any` — явное «неважно». */
export type PowertrainMode = 'unset' | 'required' | 'preferred' | 'any';

export type UsageScenario = 'city' | 'mixed' | 'autobahn' | 'long_trips' | 'family' | 'work';

export type PriorityCode =
  | 'reliability'
  | 'economy'
  | 'cargo'
  | 'comfort'
  | 'dynamics'
  | 'equipment'
  | 'lower_mileage'
  | 'newer_year'
  | 'lower_price';

export type FeatureCode =
  | 'adaptive_cruise_control'
  | 'rear_camera'
  | 'parking_sensors'
  | 'heated_seats'
  | 'apple_carplay'
  | 'android_auto'
  | 'tow_hitch'
  | 'isofix'
  | 'climate_control'
  | 'navigation';

export type EmphasisLevel = 'critical' | 'elevated' | 'normal' | 'reduced' | 'minimal';

/**
 * Компоненты Match Score (MATCHING §4.1, KARL_DIALOG_RFC §8.2).
 *
 * `wishes` появился в `mvp-1.2.0` вместе с пожеланиями: backend отдаёт восемь
 * весов, и пропущенный код здесь означал бы сырой `criteria.component.wishes`
 * в блоке «Что для вас важнее».
 */
export type ScoreComponent =
  | 'budget'
  | 'age_mileage'
  | 'usage'
  | 'priorities'
  | 'wishes'
  | 'features'
  | 'powertrain'
  | 'completeness';

export const USAGE_SCENARIOS: readonly UsageScenario[] = [
  'city',
  'mixed',
  'autobahn',
  'long_trips',
  'family',
  'work',
];

export const PRIORITY_CODES: readonly PriorityCode[] = [
  'reliability',
  'economy',
  'cargo',
  'comfort',
  'dynamics',
  'equipment',
  'lower_mileage',
  'newer_year',
  'lower_price',
];

export const FEATURE_CODES: readonly FeatureCode[] = [
  'adaptive_cruise_control',
  'rear_camera',
  'parking_sensors',
  'heated_seats',
  'apple_carplay',
  'android_auto',
  'tow_hitch',
  'isofix',
  'climate_control',
  'navigation',
];

export const BODY_TYPES: readonly BodyType[] = [
  'hatchback',
  'sedan',
  'wagon',
  'suv',
  'minivan',
  'coupe',
  'cabrio',
];

export const FUEL_TYPES: readonly FuelType[] = [
  'petrol',
  'diesel',
  'mild_hybrid',
  'full_hybrid',
  'plug_in_hybrid',
  'electric',
];

// --- Criteria Document `schema_version = 2` ---------------------------------------------

export interface CriteriaBudget {
  mode: BudgetMode | null;
  total_cents: number | null;
  reserve_cents: number;
  reserve_specified: boolean;
  /** 1.00…1.07. Больше 1.00 — только по явной фразе пользователя (MATCHING §3.2). */
  tolerance_ratio: number;
  tolerance_source_text: string | null;
  hard: true;
  confirmed: boolean;
  /** `total − reserve`, посчитано backend. Именно с ним сравнивается цена. */
  listing_budget_cents: number | null;
}

export interface CriteriaBodyTypes {
  values: BodyType[];
  mode: BodyTypesMode;
  confirmed: boolean;
}

export interface CriteriaMaxAge {
  years: number | null;
  no_limit: boolean;
  hard: true;
  confirmed: boolean;
}

export interface CriteriaFuel {
  allowed: FuelType[];
  excluded: FuelType[];
  preferred: FuelType[];
  mode: FuelMode;
}

export interface CriteriaPowertrainPreference<T> {
  value: T | null;
  mode: PowertrainMode;
}

export interface CriteriaPriority {
  code: PriorityCode;
  rank: number;
}

export interface CriteriaFeatures {
  required: FeatureCode[];
  preferred: FeatureCode[];
  specified: boolean;
}

export interface CriteriaThresholds {
  seats_min: number | null;
  trunk_volume_l_min: number | null;
  mileage_km_max: number | null;
  power_kw_min: number | null;
}

export type CriteriaEmphasis = Record<ScoreComponent, EmphasisLevel>;

export interface EmphasisSource {
  source_text: string;
  confidence: number;
}

/**
 * Марки в трёх режимах (MATCHING §3.2, §5.4, MVP_PRD §10).
 *
 * Значения — **канонические названия** справочника backend («Renault»,
 * «Mercedes-Benz», «Škoda»), а не коды: марка приходит из каталога и через
 * i18n не переводится. Поэтому интерфейс печатает их как есть.
 *
 * `required` — обязательное условие (hard-правило `MAKE_REQUIRED`, «ищу только
 * BMW»), `excluded` — обязательное условие с обратным знаком (`MAKE_EXCLUDED`),
 * `preferred` — мягкий признак, который **не даёт баллов**.
 *
 * `confirmed` — обязательность подтверждена самим пользователем. Пока она
 * ложна, подбор не запускается: «только BMW» и «BMW — моя мечта» звучат похоже
 * и означают разное (BE-392).
 */
export interface CriteriaMakes {
  required: string[];
  preferred: string[];
  excluded: string[];
  confirmed: boolean;
}

/**
 * Модель автомобиля как критерий (KARL_DIALOG_RFC §6.1).
 *
 * `make` и `model` — канонические названия справочника, а не коды: как у марок,
 * через i18n они не переводятся и печатаются как есть. `generation` — строка
 * («8», «F31») или `null`.
 */
export interface ModelRef {
  make: string;
  model: string;
  generation: string | null;
}

/**
 * Модели в трёх режимах — по образцу марок.
 *
 * `required` — hard-правило `MODEL_REQUIRED` («только Golf»), `excluded` —
 * `MODEL_EXCLUDED`, `preferred` — tie-break и объяснение `PREFERRED_MODEL`,
 * баллов не даёт. `confirmed` — пользователь ответил на «только эта или похожие
 * тоже»; пока он ложен при названной модели, подбор не запускается.
 */
export interface CriteriaModels {
  required: ModelRef[];
  preferred: ModelRef[];
  excluded: ModelRef[];
  confirmed: boolean;
}

/** Закрытая таксономия пожеланий (KARL_DIALOG_RFC §6.2) — 26 тегов. */
export type WishTag =
  | 'safe'
  | 'reliable'
  | 'robust'
  | 'economical'
  | 'cheap_to_run'
  | 'eco'
  | 'comfortable'
  | 'quiet'
  | 'spacious'
  | 'practical'
  | 'family'
  | 'compact'
  | 'easy_to_park'
  | 'city'
  | 'agile'
  | 'sporty'
  | 'fast'
  | 'powerful'
  | 'long_distance'
  | 'autobahn'
  | 'towing'
  | 'offroad'
  | 'high_seating'
  | 'premium'
  | 'prestigious'
  | 'modern_tech'
  | 'specific_model';

/** «Хотелось бы» / «важно» / «главное» — вес пожелания, но не жёсткость. */
export type WishStrength = 'mention' | 'important' | 'critical';

/**
 * Пожелание с цитатой пользователя.
 *
 * Цитата обязательна: пожелание без неё не существует (CLAUDE.md §6.1).
 * Пожелание **взвешивает** автомобили, а не отсеивает их — «безопасная» не
 * становится обязательным условием ни при какой силе (MATCHING §3.5).
 */
export interface CriteriaWish {
  tag: WishTag;
  strength: WishStrength;
  source_text: string;
}

/** Способ покупки (MVP_PRD §46). На подбор **не влияет** (MATCHING §3). */
export type PurchaseMethod = 'cash' | 'financing' | 'leasing' | 'unknown';

/**
 * Автомобиль в зачёт: только описание со слов пользователя.
 *
 * Оценки нет и не будет — CarViQ.DE не оценивает автомобиль в trade-in
 * (MVP_PRD §46.3, CLAUDE.md §2).
 */
export interface CriteriaTradeIn {
  present: boolean;
  description: string | null;
}

export interface CriteriaPurchase {
  method: PurchaseMethod;
  /** Только при `financing` и `leasing`; в бюджет автомобиля не пересчитывается. */
  monthly_budget_cents: number | null;
  down_payment_cents: number | null;
  trade_in: CriteriaTradeIn;
}

/** `stepwise` — порядок набора; `required` — hard-критерий «только моя земля». */
export type GeographyMode = 'stepwise' | 'required';

export interface CriteriaGeography {
  country_code: 'DE';
  /** Домашняя земля пользователя. `null` — не называл, поиск по всей стране. */
  home_state_code: string | null;
  home_city: string | null;
  state_codes: string[];
  mode: GeographyMode;
  specified: boolean;
}

export interface UnresolvedQuestion {
  code: string;
  path: string | null;
  params: Record<string, unknown>;
}

/** Что patch engine отбросил или ограничил (`EMPHASIS_CAPPED` и т. п.). */
export interface CriteriaWarning {
  code: string;
  path: string | null;
  detail: string | null;
}

export interface CriteriaPayload {
  schema_version: 4;
  locale: string;
  budget: CriteriaBudget;
  body_types: CriteriaBodyTypes;
  max_age: CriteriaMaxAge;
  annual_mileage_km: number | null;
  usage: UsageScenario[];
  fuel: CriteriaFuel;
  transmission: CriteriaPowertrainPreference<Transmission>;
  drivetrain: CriteriaPowertrainPreference<Drivetrain>;
  priorities: CriteriaPriority[];
  features: CriteriaFeatures;
  thresholds: CriteriaThresholds;
  emphasis: CriteriaEmphasis;
  emphasis_sources: Partial<Record<ScoreComponent, EmphasisSource>>;
  makes: CriteriaMakes;
  /** Модель как критерий — v4 (KARL_DIALOG_RFC §6.1). */
  models: CriteriaModels;
  /** Пожелания с цитатами — v4 (KARL_DIALOG_RFC §6.2). */
  wishes: CriteriaWish[];
  /**
   * Что пользователь назвал сам, а что выведено из пожеланий (RFC §6.3).
   *
   * Экран критериев обязан их различать: выведенное помечается «из ваших слов»
   * и снимается вместе с пожеланиями, названное — нет.
   */
  priorities_explicit: boolean;
  priorities_derived_from_wishes: boolean;
  usage_explicit: boolean;
  usage_derived_from_wishes: boolean;
  geography: CriteriaGeography;
  /** Способ покупки. В подборе не участвует ни одним полем (MATCHING §3). */
  purchase: CriteriaPurchase;
  unresolved_questions: UnresolvedQuestion[];
  warnings: CriteriaWarning[];
}

// --- Вопросы планировщика и веса --------------------------------------------------------

export interface QuickReply {
  code: string;
  /** Ключ i18n короткой метки кнопки. */
  label_key: string;
  /** Фраза, которую backend предлагает отправить сообщением. */
  payload: string;
  /** «Неважно»: вместе с конкретными значениями своей группы не имеет смысла. */
  exclusive: boolean;
}

/** Группа вариантов внутри вопроса — один критерий (MVP_PRD §9). */
export interface QuestionGroup {
  slot: string;
  label_key: string;
  selection: 'single' | 'multi';
  /** Группу можно пропустить: на готовность она не влияет. */
  optional: boolean;
  quick_replies: QuickReply[];
}

export interface PlannedQuestion {
  question_code: string;
  text_key: string;
  params: Record<string, unknown>;
  /** Больше нуля — тот же вопрос уже задавался, ответ до критериев не дошёл. */
  repeat_index: number;
  groups: QuestionGroup[];
}

/** Строка блока «Что для вас важнее» (MVP_PRD §11). Числа считает backend. */
export interface WeightPreview {
  component: ScoreComponent;
  level: EmphasisLevel;
  base_weight: number;
  configured_weight: number;
  share_percent: number;
  /** Цитата пользователя, из которой выведен акцент. `null` — акцента нет. */
  source_text: string | null;
}

/**
 * Требование, действовавшее на прогоне как желательное (KARL_DIALOG_RFC §6.4).
 *
 * Критерии пользователя при этом **не менялись**: демоция — свойство прогона.
 * Правило hard-фильтра не отличает «нет опции» от «нет данных», а отсеивать за
 * отсутствие данных нельзя — при пустом справочнике `isofix required` давал бы
 * ноль результатов. На следующем прогоне с выросшим покрытием требование снова
 * отсеивает машины.
 */
export interface Demotion {
  code: 'FEATURE_DEMOTED_LOW_COVERAGE' | 'THRESHOLD_DEMOTED_LOW_COVERAGE';
  path: string;
  /** Код признака оснащения или имя поля порога. */
  value: string;
  /** Доля опубликованных машин, у которых поле заполнено: `0.07` — 7 %. */
  coverage: number;
}

export interface CriteriaState {
  version_id: string;
  version_number: number;
  readiness_score: number;
  /** `true` ровно при `readiness_score = 100` — поиск разрешён только тогда. */
  ready_for_confirmation: boolean;
  payload: CriteriaPayload;
  next_question: PlannedQuestion | null;
  weights: WeightPreview[];
  /** Демоции последнего прогона. Пусто — прогона не было или смягчать нечего. */
  demotions: Demotion[];
}

// --- Чат --------------------------------------------------------------------------------

export type ChatMessageRole = 'user' | 'assistant';
export type ChatMessageStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface CriteriaChange {
  path: string;
  old_value: unknown;
  new_value: unknown;
}

/**
 * Метаданные реплики ассистента. У сообщения пользователя объект пустой,
 * поэтому все поля необязательные.
 */
export interface ChatMessageMetadata {
  intent?: string;
  out_of_scope?: boolean;
  question_code?: string | null;
  text_key?: string;
  params?: Record<string, unknown>;
  question_repeat?: number;
  quick_replies?: Pick<QuickReply, 'code' | 'label_key' | 'payload'>[];
  changes?: CriteriaChange[];
  llm_call_id?: string | null;
  fallback?: boolean;
  /** Реплика сформулирована моделью (BE-381). Служебное поле, на экран не идёт. */
  responder_rendered?: boolean;
  /** Почему показан фиксированный текст. Пользователю не показывается (FE-380). */
  responder_fallback_reason?: string | null;
  responder_violated_rules?: string[];
}

export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  /** У ассистента — ключ i18n, у пользователя — введённый текст. */
  content: string;
  /**
   * Показанный текст реплики Karl (BE-381, MVP_TECH_RFC §9.2).
   *
   * `null` — реплику формулировал не разговорный слой: контур выключен, модель
   * не ответила или ответ не прошёл детерминированные проверки. Тогда
   * показывается перевод `content` — прежний фиксированный текст вопроса.
   * Различать эти два случая на экране **нельзя**: fallback выглядит как
   * обычная реплика, никаких «ошибка генерации» пользователь не видит.
   */
  rendered_text: string | null;
  status: ChatMessageStatus;
  sequence_number: number;
  client_message_id: string | null;
  criteria_version_id: string | null;
  metadata: ChatMessageMetadata;
  created_at: string;
  completed_at: string | null;
}

/**
 * Статусы запроса (MVP_PRD §20). Каждый — отдельная карточка со своим главным
 * действием, поэтому склеивать их на фронте нельзя:
 *
 * * `draft` — «диалог не завершён» + прогресс готовности;
 * * `ready` — «критерии подтверждены, подбор не запускался»;
 * * `running` — прогон в очереди или выполняется;
 * * `results_ready` — «N автомобилей» + лучший score;
 * * `stale` — «каталог обновился, результаты могли измениться»;
 * * `failed` — «подбор не удался»;
 * * `archived` — только чтение.
 */
export type SearchSessionStatus =
  'draft' | 'ready' | 'running' | 'results_ready' | 'stale' | 'failed' | 'archived';

/**
 * Исход покупки со слов пользователя (MVP_PRD §39.3).
 *
 * `null` — отметки нет, и это **не** покупка: молчание не согласие. Только
 * `purchased` запускает расчёт вознаграждения (§42.4).
 */
export type PurchaseOutcome = 'purchased' | 'not_purchased' | 'undecided';

export const SEARCH_SESSION_SORTS = ['activity_desc', 'created_desc', 'title_asc'] as const;
export type SearchSessionSort = (typeof SEARCH_SESSION_SORTS)[number];

/**
 * Заказ (BE-150, BE-200).
 *
 * Два состояния приходят рядом и **не схлопываются**: `status` описывает прогон
 * по каталогу, `stage` — заказ целиком (оплату, отбор, проверку, торг). Шаг
 * трекера считает чистая функция `stageToStep`, а не backend.
 */
export interface SearchSession {
  id: string;
  status: SearchSessionStatus;
  /** Стадия воронки (MVP_PRD §33.2). Значения — `ORDER_STAGES`. */
  stage: OrderStage;
  /**
   * Оплачен ли заказ. Отдельно от стадии: правка критериев возвращает
   * оплаченный заказ к анализу, но замки на шаги 4–7 не возвращаются —
   * платить второй раз не нужно (MVP_PRD §35.4).
   */
  paid: boolean;
  paid_at: string | null;
  /** Причина закрытия заказа. `null` — заказ не закрыт (MVP_PRD §33.2). */
  close_reason: string | null;
  /** Автомобиль, выбранный на шаге 4. Проставляется до торга (§34.6). */
  selected_vehicle_id: string | null;
  selected_at: string | null;
  /** Исход покупки **со слов пользователя** (§39.3). `null` — отметки нет. */
  purchase_outcome: PurchaseOutcome | null;
  /** Сколько раз автомобиль в четвёрке заменялся (§34.13). */
  replacement_count: number;
  /** Сколько автомобилей заказа всего проверено (§34.13). */
  checked_vehicle_count: number;
  /** `null` — критериев ещё нет и первого сообщения тоже: заглушка, не текст. */
  title: string | null;
  /** `true` — имя задал пользователь, автогенерация его не перезапишет. */
  title_is_custom: boolean;
  initial_prompt: string | null;
  current_criteria_version_id: string | null;
  current_search_run_id: string | null;
  /** Оптимистическая блокировка: уходит обратно в каждом PATCH. */
  version: number;
  created_at: string;
  updated_at: string;
  last_activity_at: string;
  archived_at: string | null;
}

/** Карточка списка «Мои заказы»: запрос + агрегаты прогона и переговоров. */
export interface SearchSessionSummary {
  session: SearchSession;
  /** `null` — версии критериев ещё нет; это не то же самое, что пустые критерии. */
  criteria: CriteriaPayload | null;
  readiness_score: number | null;
  run_status: string | null;
  run_completed_at: string | null;
  result_count: number | null;
  eligible_count: number | null;
  best_score: number | null;
  /**
   * Счётчики переговоров по статусам. До Epic 17 объект пуст: переговоров
   * физически нет, и показывать «0 переговоров» на каждой карточке незачем.
   * Число ожидающих решения пользователя берётся отсюда по ключу
   * `awaiting_user` и отдельным полем не дублируется.
   */
  negotiation_counts: Record<string, number>;
  /**
   * Сколько автомобилей сейчас в четвёрке (Epic 22). `null` — отбор ещё не
   * выполнялся: это не то же самое, что «ноль», и рисовать вместо него ноль
   * было бы враньём о состоянии заказа.
   */
  selection_count: number | null;
}

export interface SearchSessionsPage {
  items: SearchSessionSummary[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  active_count: number;
  archived_count: number;
  /** `MAX_ACTIVE_SEARCHES` с backend: число в тексте лимита не хардкодится. */
  limit: number;
}

/** Кандидат на архивирование из `409 SEARCH_LIMIT_REACHED` (MVP_PRD §20). */
export interface ArchiveCandidate {
  id: string;
  title: string | null;
  status: SearchSessionStatus;
  last_activity_at: string;
}

export interface SearchLimitContext {
  limit: number;
  archive_candidates: ArchiveCandidate[];
}

/**
 * Предложение расширить географию (BE-395, MVP_PRD §43.3).
 *
 * Числа настоящие и посчитаны тем же прогоном, который собрал показанный
 * набор: «в вашей земле 12, соседние добавят 34». Литералов в тексте нет.
 */
export interface GeoExpansionOffer {
  /** 2 — соседние земли, 3 — вся страна. */
  next_step: number;
  /** `neighbours` | `country` — ключ i18n. Номер ступени пользователю не нужен. */
  next_step_code: string;
  would_add: number;
  current_count: number;
}

export interface SessionState {
  session: SearchSession;
  messages: ChatMessage[];
  criteria: CriteriaState;
  /**
   * Заказ ждёт перестройки подбора (BE-355). То же правило, что и в `ChatTurn`:
   * предложение обязано пережить перезагрузку страницы, а не жить только в
   * ответе на сообщение.
   */
  rebuild_pending: boolean;
  /**
   * Предложение расширить географию. `null` — вопроса нет, и строка о географии
   * в рельсе не рисуется. Правило считает backend по тем же причинам, что и
   * `rebuild_pending`: два вывода одного правила однажды разойдутся.
   */
  geo_expansion_offer: GeoExpansionOffer | null;
}

export interface ChatTurn {
  session_id: string;
  user_message: ChatMessage;
  /** `null` при отказе LLM — вместе с `fallback_required = true` (§9). */
  assistant_message: ChatMessage | null;
  criteria: CriteriaState;
  changes: CriteriaChange[];
  ready_for_confirmation: boolean;
  fallback_required: boolean;
  replayed: boolean;
  /**
   * Заказ ждёт перестройки подбора по новым критериям (BE-355, MVP_PRD §35.4).
   *
   * Считает backend: правило одно, и выводить его на фронте из `status` и
   * `stage` значит однажды получить два разных ответа на один вопрос. Сам флаг
   * ничего не запускает — прогон ставит подтверждение пользователя.
   */
  rebuild_pending: boolean;
}

/** Одна операция ручной правки. Пути — из allowlist backend (MVP_TECH_RFC §9). */
export interface CriteriaPatchOperation {
  path: string;
  value: unknown;
}
